import * as React from 'react';
import { TextareaAutosize as BaseTextareaAutosize } from '@mui/base';
import clsx from 'clsx';

interface Validation {
  message: string;
}

interface InputProps {
  name?: string;
  label?: string | React.ReactNode;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  maxLength?: number;
  styles?: React.CSSProperties;
  className?: string;
  validation?: Validation;
  minRows?: number;
}

const TextareaAutosize = React.forwardRef<HTMLTextAreaElement, InputProps>(function TextareaAutosize(props, ref) {
  const {
    name,
    label,
    required = false,
    placeholder,
    disabled = false,
    value: externalValue,
    maxLength,
    onChange: customOnChange,
    onBlur: customOnBlur,
    onFocus: customOnFocus,
    onKeyDown: customKeyDown,
    className,
    validation,
    minRows = 1,
  } = props;

  const [value, setValue] = React.useState('');

  React.useEffect(() => {
    setValue(externalValue ?? '');
  }, [externalValue]);

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);

    if (customOnChange) {
      customOnChange(e);
    }
  };

  const onBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    customOnBlur && customOnBlur(e);
  };

  const onFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    customOnFocus && customOnFocus(e);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    customKeyDown && customKeyDown(e);
  };

  const customLabel = () => {
    if (typeof label === 'string' || React.isValidElement(label)) {
      return React.cloneElement(<label className="c-input-label">{label}</label>, { required });
    }
  };

  const InputCls = clsx({
    'c-input': true,
    'c-input-textarea': true,
    [className]: !!className,
  });

  const InputWrapCls = clsx({
    'c-input-validation c-input-validation--error': validation?.message,
    [className]: !!className,
  });

  return (
    <div className={InputWrapCls}>
      {customLabel()}
      <BaseTextareaAutosize
        name={name}
        maxLength={maxLength}
        className={InputCls}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        ref={ref}
        minRows={minRows}
      />
      {validation?.message && (
        <div className="c-input-validation c-input-validation--error">
          <span className="c-input__validation-icon"></span>
          <span className="c-input__validation-message">{validation?.message}</span>
        </div>
      )}
    </div>
  );
});

export { TextareaAutosize };
